import { useEffect, useState, useCallback } from "react";
import firebase from "../firebase";
import DB_PATH from "../DB_PATH";
import { ReelCharacter } from "../../types/ReelCharacter";

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const stripUndefined = <T extends Record<string, any>>(obj: T): T => {
  const out: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined) out[key] = obj[key];
  });
  return out as T;
};

const charactersCollection = (communityId: string) =>
  firebase
    .firestore()
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.REEL_CHARACTER);

// Live cast library for a community plus save/archive/delete helpers. Mirrors
// the MCP server repo (mcp/src/repos/reelCharacters.ts). Keep write shapes in sync.
const useReelCharacters = ({ communityId }: { communityId: string }) => {
  const [characters, setCharacters] = useState<ReelCharacter[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!communityId) {
      setCharacters([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const unsubscribe = charactersCollection(communityId)
      .orderBy("updatedAt", "desc")
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map(
            (doc) => ({ ...(doc.data() as ReelCharacter), id: doc.id })
          );
          setCharacters(data);
          setIsLoading(false);
        },
        (error) => {
          console.error("Error fetching reel characters:", error);
          setIsLoading(false);
        }
      );
    return () => unsubscribe();
  }, [communityId]);

  const saveCharacter = useCallback(
    async (
      character: Partial<ReelCharacter> & { name: string }
    ): Promise<string> => {
      const collection = charactersCollection(communityId);
      const ref = character.id ? collection.doc(character.id) : collection.doc();
      const isNew = !character.id;
      const payload = stripUndefined({
        ...character,
        id: ref.id,
        slug: character.slug || slugify(character.name),
        updatedAt: new Date().toISOString(),
        ...(isNew
          ? { createdAt: new Date().toISOString(), status: "active" as const }
          : {}),
      });
      await ref.set(payload, { merge: true });
      return ref.id;
    },
    [communityId]
  );

  const archiveCharacter = useCallback(
    async (characterId: string, archived: boolean) => {
      await charactersCollection(communityId).doc(characterId).set(
        {
          status: archived ? "archived" : "active",
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
    },
    [communityId]
  );

  const deleteCharacter = useCallback(
    async (characterId: string) => {
      await charactersCollection(communityId).doc(characterId).delete();
    },
    [communityId]
  );

  return {
    characters,
    activeCharacters: characters.filter((c) => c.status !== "archived"),
    isLoading,
    saveCharacter,
    archiveCharacter,
    deleteCharacter,
  };
};

export default useReelCharacters;
